import { useState } from 'react';
import {
  INVOICE_STATUSES,
  INVOICE_STATUS_LABELS,
  type Employee,
  type InvoiceListQuery,
} from '@faktorplus/shared';
import {
  useCostCategories,
  useEmployees,
  useProjects,
  useVendors,
} from '../../api/dictionaries';
import { Button } from '../../components/ui/button';
import { FilterIcon } from '../../components/ui/icons';
import { Field, Input, Select } from '../../components/ui/form';
import { Modal, modalStyles } from '../../components/ui/modal';
import { formatDate } from '../../lib/format';
import styles from './invoice-filters.module.css';

/**
 * The register's filter bar.
 *
 * Five controls in a row wrapped onto three lines and pushed the table below
 * the fold, so everything except the search box now lives behind one button.
 * Search stays out in the open because it is the one filter people retype all
 * day, and every hidden filter comes back as a chip — a filtered register that
 * looks unfiltered is how you send someone the wrong CSV.
 */

/** The keys the dialog owns. `search` is deliberately not one of them. */
const DIALOG_KEYS = [
  'projectId',
  'vendorId',
  'categoryId',
  'employeeIds',
  'status',
  'dueFrom',
  'dueTo',
  'onlyUnpaid',
  'all',
  'includeArchived',
] as const;

type DialogKey = (typeof DIALOG_KEYS)[number];

/**
 * Every filter as a string, `employeeIds` included — it holds a comma-separated
 * list. One representation for the draft, the URL and the API keeps the three
 * from drifting apart.
 */
type Draft = Record<DialogKey, string>;

type SelectKey = Exclude<
  DialogKey,
  'onlyUnpaid' | 'all' | 'includeArchived' | 'dueFrom' | 'dueTo' | 'employeeIds'
>;

interface SelectDef {
  key: SelectKey;
  label: string;
  /** The "no choice made" option, e.g. "Wszystkie budowy". */
  anyLabel: string;
  options: { value: string; label: string }[];
}

const UNPAID_LABEL = 'Tylko niezapłacone';
const SHOW_ALL_LABEL = 'Pokaż też rozliczone';
const ARCHIVED_LABEL = 'Pokaż też budowy archiwalne';

interface InvoiceFiltersProps {
  params: URLSearchParams;
  onChange: (patch: Record<string, string>) => void;
  onReset: () => void;
  /** Named after the list being filtered, because the dialog says so out loud. */
  title?: string;
  /**
   * Payables are unpaid by definition, so there the toggle would offer a choice
   * that does not exist. Everywhere else it is one of the filters.
   */
  showUnpaidFilter?: boolean;
  /**
   * The payables counterpart: widen the list to the settled invoices too
   * (24.09.2026). A filter like any other — with a chip, counted on the
   * button — rather than a stray checkbox in the header.
   */
  showSettledFilter?: boolean;
  /**
   * The register's escape hatch into archived sites' invoices (24.09.2026).
   * Payables switch it off: nothing archived is ever owed.
   */
  showArchivedFilter?: boolean;
}

export function InvoiceFilters({
  params,
  onChange,
  onReset,
  title = 'Filtry rejestru',
  showUnpaidFilter = true,
  showSettledFilter = false,
  showArchivedFilter = true,
}: InvoiceFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const defs = useSelectDefs();
  const { data: employees = [] } = useEmployees();

  const activeCount = countActive(params);
  const chips = describeActive(params, defs, employees);

  return (
    <>
      <div className={styles.bar}>
        <Input
          className={styles.search}
          placeholder="Szukaj po numerze, kontrahencie lub pozycji księgowej…"
          aria-label="Szukaj"
          value={params.get('search') ?? ''}
          onChange={(event) => onChange({ search: event.target.value })}
        />

        <Button
          className={activeCount > 0 ? styles.ctaActive : undefined}
          onClick={() => setIsOpen(true)}
          aria-expanded={isOpen}
        >
          <FilterIcon className={styles.filterIcon} />
          Filtry
          {activeCount > 0 && <span className={styles.count}>{activeCount}</span>}
        </Button>
      </div>

      {chips.length > 0 && (
        <div className={styles.chips}>
          {chips.map((chip) => (
            <span key={chip.key} className={styles.chip}>
              <span className={styles.chipLabel}>{chip.label}</span>
              <span className={styles.chipValue}>{chip.value}</span>
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Usuń filtr: ${chip.label} ${chip.value}`}
                onClick={() => onChange(chip.clear)}
              >
                ×
              </button>
            </span>
          ))}
          <button type="button" className={styles.clearAll} onClick={onReset}>
            Wyczyść wszystkie
          </button>
        </div>
      )}

      {isOpen && (
        <FilterDialog
          defs={defs}
          employees={employees}
          title={title}
          showUnpaidFilter={showUnpaidFilter}
          showSettledFilter={showSettledFilter}
          showArchivedFilter={showArchivedFilter}
          initial={readDraft(params)}
          onApply={(draft) => {
            onChange(draft);
            setIsOpen(false);
          }}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
}

interface FilterDialogProps {
  defs: SelectDef[];
  employees: Employee[];
  title: string;
  showUnpaidFilter: boolean;
  showSettledFilter: boolean;
  showArchivedFilter: boolean;
  initial: Draft;
  onApply: (draft: Draft) => void;
  onClose: () => void;
}

/**
 * Edits a draft and commits it on "Zastosuj". Writing straight through to the
 * URL would refetch the register on every dropdown touch while the reader is
 * still assembling the question they want to ask.
 */
function FilterDialog({
  defs,
  employees,
  title,
  showUnpaidFilter,
  showSettledFilter,
  showArchivedFilter,
  initial,
  onApply,
  onClose,
}: FilterDialogProps) {
  const [draft, setDraft] = useState<Draft>(initial);
  const pickedEmployees = splitIds(draft.employeeIds);

  function set(key: DialogKey, value: string) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function toggleEmployee(employeeId: string) {
    const next = pickedEmployees.includes(employeeId)
      ? pickedEmployees.filter((id) => id !== employeeId)
      : [...pickedEmployees, employeeId];

    set('employeeIds', next.join(','));
  }

  return (
    <Modal
      title={title}
      isOpen
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={() => setDraft(emptyDraft())}>
            Wyczyść
          </Button>
          <span className={modalStyles.footerSpacer} />
          <Button onClick={onClose}>Anuluj</Button>
          <Button variant="primary" onClick={() => onApply(draft)}>
            Zastosuj
          </Button>
        </>
      }
    >
      {defs.map((def) => (
        <Field key={def.key} label={def.label}>
          {(id) => (
            <Select id={id} value={draft[def.key]} onChange={(e) => set(def.key, e.target.value)}>
              <option value="">{def.anyLabel}</option>
              {def.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      ))}

      {employees.length > 0 && (
        <Field
          label="Pracownicy"
          hint="Zaznacz kilka osób, żeby zobaczyć ich koszty razem."
        >
          {() => (
            <div className={styles.checkList}>
              {employees.map((employee) => (
                <label key={employee.id} className={styles.checkItem}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={pickedEmployees.includes(employee.id)}
                    onChange={() => toggleEmployee(employee.id)}
                  />
                  {employee.fullName}
                </label>
              ))}
            </div>
          )}
        </Field>
      )}

      <Field label="Termin płatności">
        {(id) => (
          <div className={styles.range}>
            <Input
              id={id}
              type="date"
              aria-label="Termin płatności od"
              max={draft.dueTo || undefined}
              value={draft.dueFrom}
              onChange={(event) => set('dueFrom', event.target.value)}
            />
            <span className={styles.rangeSeparator}>—</span>
            <Input
              type="date"
              aria-label="Termin płatności do"
              min={draft.dueFrom || undefined}
              value={draft.dueTo}
              onChange={(event) => set('dueTo', event.target.value)}
            />
          </div>
        )}
      </Field>

      {showUnpaidFilter && (
        <label className={styles.toggle}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={draft.onlyUnpaid === 'true'}
            onChange={(event) => set('onlyUnpaid', event.target.checked ? 'true' : '')}
          />
          {UNPAID_LABEL}
        </label>
      )}

      {showSettledFilter && (
        <label className={styles.toggle}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={draft.all === 'true'}
            onChange={(event) => set('all', event.target.checked ? 'true' : '')}
          />
          {SHOW_ALL_LABEL}
        </label>
      )}

      {showArchivedFilter && (
        <label className={styles.toggle}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={draft.includeArchived === 'true'}
            onChange={(event) => set('includeArchived', event.target.checked ? 'true' : '')}
          />
          {ARCHIVED_LABEL}
        </label>
      )}
    </Modal>
  );
}

/** One definition per dropdown, so the dialog and the chips cannot drift. */
function useSelectDefs(): SelectDef[] {
  const { data: projects = [] } = useProjects();
  const { data: vendors = [] } = useVendors();
  const { data: categories = [] } = useCostCategories();

  return [
    {
      key: 'projectId',
      label: 'Budowa',
      anyLabel: 'Wszystkie budowy',
      options: projects.map((project) => ({ value: project.id, label: project.name })),
    },
    {
      key: 'vendorId',
      label: 'Kontrahent',
      anyLabel: 'Wszyscy kontrahenci',
      options: vendors.map((vendor) => ({ value: vendor.id, label: vendor.name })),
    },
    {
      key: 'categoryId',
      label: 'Kategoria',
      anyLabel: 'Wszystkie kategorie',
      options: categories.map((category) => ({ value: category.id, label: category.name })),
    },
    {
      key: 'status',
      label: 'Status',
      anyLabel: 'Wszystkie statusy',
      options: INVOICE_STATUSES.map((status) => ({
        value: status,
        label: INVOICE_STATUS_LABELS[status],
      })),
    },
  ];
}

/**
 * The URL read back as the query the API is asked for.
 *
 * Lives next to the controls that write those keys, so the register and the
 * payables screen cannot end up disagreeing about what `dueTo` means.
 * Paging and sorting are left out: each list defaults them differently.
 */
export function readInvoiceFilters(params: URLSearchParams): Partial<InvoiceListQuery> {
  const status = params.get('status');

  return {
    search: params.get('search') || undefined,
    projectId: params.get('projectId') || undefined,
    vendorId: params.get('vendorId') || undefined,
    categoryId: params.get('categoryId') || undefined,
    employeeIds: splitIds(params.get('employeeIds')),
    status: INVOICE_STATUSES.find((candidate) => candidate === status),
    dueFrom: readDate(params.get('dueFrom')),
    dueTo: readDate(params.get('dueTo')),
    ...(params.get('onlyUnpaid') === 'true' ? { onlyUnpaid: true } : {}),
    ...(params.get('includeArchived') === 'true' ? { includeArchived: true } : {}),
    ...(params.get('onlyCash') === 'true' ? { onlyCash: true } : {}),
    ...(params.get('missingProtocol') === 'true' ? { missingProtocol: true } : {}),
    ...(params.get('withoutOwnerApproval') === 'true' ? { withoutOwnerApproval: true } : {}),
  };
}

/** Filters the dashboard links set; they have chips but no control in the dialog. */
const LINK_ONLY_KEYS = ['onlyCash', 'missingProtocol', 'withoutOwnerApproval'] as const;

/** Whether anything is narrowing the list — an empty result then means "no match". */
export function hasAnyInvoiceFilter(params: URLSearchParams): boolean {
  return ['search', ...DIALOG_KEYS, ...LINK_ONLY_KEYS].some((key) => params.get(key));
}

function readDate(value: string | null): Date | undefined {
  return value ? new Date(value) : undefined;
}

/**
 * The comma-separated list as ids. An empty result serialises back to an empty
 * query value, which `buildQueryString` drops — so "nobody picked" widens the
 * list rather than narrowing it to nothing.
 */
function splitIds(value: string | null | undefined): string[] {
  return (value ?? '').split(',').filter(Boolean);
}

interface Chip {
  key: string;
  label: string;
  value: string;
  /** The patch that removes this filter — a range clears both of its ends. */
  clear: Record<string, string>;
}

function describeActive(
  params: URLSearchParams,
  defs: SelectDef[],
  employees: Employee[],
): Chip[] {
  const chips: Chip[] = [];

  for (const def of defs) {
    const value = params.get(def.key);
    if (!value) continue;

    // A dictionary entry can be deactivated while a saved link still names it.
    const option = def.options.find((candidate) => candidate.value === value);
    chips.push({
      key: def.key,
      label: def.label,
      value: option?.label ?? 'nieznana wartość',
      clear: { [def.key]: '' },
    });
  }

  const pickedEmployees = splitIds(params.get('employeeIds'));
  if (pickedEmployees.length > 0) {
    // Named while there are few enough to read; counted once the chip would be
    // longer than the row it sits in.
    const names = pickedEmployees.map(
      (id) => employees.find((employee) => employee.id === id)?.fullName ?? 'nieznany',
    );

    chips.push({
      key: 'employeeIds',
      label: pickedEmployees.length === 1 ? 'Pracownik' : 'Pracownicy',
      value: names.length <= 3 ? names.join(', ') : `${names.length} osób`,
      clear: { employeeIds: '' },
    });
  }

  const dueRange = describeDueRange(params);
  if (dueRange) {
    chips.push({
      key: 'due',
      label: 'Termin płatności',
      value: dueRange,
      clear: { dueFrom: '', dueTo: '' },
    });
  }

  if (params.get('onlyUnpaid') === 'true') {
    chips.push({
      key: 'onlyUnpaid',
      label: 'Rozliczenie',
      value: UNPAID_LABEL,
      clear: { onlyUnpaid: '' },
    });
  }

  if (params.get('all') === 'true') {
    chips.push({
      key: 'all',
      label: 'Zakres',
      value: 'z rozliczonymi',
      clear: { all: '' },
    });
  }

  if (params.get('includeArchived') === 'true') {
    chips.push({
      key: 'includeArchived',
      label: 'Zakres',
      value: 'z budowami archiwalnymi',
      clear: { includeArchived: '' },
    });
  }

  if (params.get('onlyCash') === 'true') {
    chips.push({
      key: 'onlyCash',
      label: 'Forma płatności',
      value: 'Gotówka',
      clear: { onlyCash: '' },
    });
  }

  if (params.get('missingProtocol') === 'true') {
    chips.push({
      key: 'missingProtocol',
      label: 'Dokumenty',
      value: 'Brak protokołu',
      clear: { missingProtocol: '' },
    });
  }

  if (params.get('withoutOwnerApproval') === 'true') {
    chips.push({
      key: 'withoutOwnerApproval',
      label: 'Zatwierdzenie',
      value: 'Bez akceptacji właściciela',
      clear: { withoutOwnerApproval: '' },
    });
  }

  return chips;
}

/** One chip for both ends, phrased as whichever ends are actually set. */
function describeDueRange(params: URLSearchParams): string | null {
  const from = params.get('dueFrom');
  const to = params.get('dueTo');

  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  if (from) return `od ${formatDate(from)}`;
  if (to) return `do ${formatDate(to)}`;
  return null;
}

/**
 * Counts only what the dialog hides — the search box speaks for itself, and a
 * date range is one filter however many of its ends are filled in.
 */
function countActive(params: URLSearchParams): number {
  const singles = DIALOG_KEYS.filter(
    (key) => key !== 'dueFrom' && key !== 'dueTo' && params.get(key),
  ).length;

  return singles + (params.get('dueFrom') || params.get('dueTo') ? 1 : 0);
}

function readDraft(params: URLSearchParams): Draft {
  return Object.fromEntries(DIALOG_KEYS.map((key) => [key, params.get(key) ?? ''])) as Draft;
}

function emptyDraft(): Draft {
  return Object.fromEntries(DIALOG_KEYS.map((key) => [key, ''])) as Draft;
}
